import React from 'react';
import PropTypes from 'prop-types';
import Layout from '../../components/Layout/Layout';
import PageSection from '../../components/Layout/PageSection/PageSection';
import { SnowplowPageViewTracker } from '../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import './PrivacyPolicy.scss';

export default class PrivacyPolicy extends React.PureComponent {
    render() {
        const { PAGE_PROPS } = this.props;
        return (
            <div className="page-PrivacyPolicy">
                <SnowplowPageViewTracker />
                <Layout PAGE_PROPS={PAGE_PROPS}>
                    <PageSection className="privacy-policy-main">
                        <h2
                            className="main-headline"
                            id="fairygodboss-privacy-policy"
                        >
                            Fairygodboss Privacy Policy
                        </h2>
                        <p className="effective-date">
                            <strong>Effective Date: June 23, 2023</strong>
                        </p>
                        <p>
                            This “<strong>Privacy Policy</strong>” describes the
                            privacy practices of FGB Muse Group Inc. and our
                            subsidiaries and affiliates (collectively, “
                            <strong>Fairygodboss</strong>,” “<strong>we</strong>
                            ”, “<strong>us</strong>”, or “<strong>our</strong>”)
                            in connection with the &nbsp;
                            <a href="/">http://www.fairygodboss.com</a>&nbsp;
                            website and any other website that we own or control
                            and which posts or links to this Privacy Policy
                            (collectively, the “<strong>Service”</strong>), and
                            the rights and choices available to individuals with
                            respect to their information.
                        </p>
                        <h3 id="table-of-contents">Table of Contents</h3>
                        <ul>
                            <li>
                                <a href="#personal-information-we-collect">
                                    Personal Information We Collect
                                </a>
                            </li>
                            <li>
                                <a href="#how-we-use-personal-info">
                                    How We Use Your Personal Information
                                </a>
                            </li>
                            <li>
                                <a href="#how-we-share-personal-info">
                                    How We Share Your Personal Information
                                </a>
                            </li>
                            <li>
                                <a href="#your-choices">Your Choices</a>
                            </li>
                            <li>
                                <a href="#third-party-sites-and-services">
                                    Third Party Sites and Services
                                </a>
                            </li>
                            <li>
                                <a href="#security-practices">
                                    Security Practices
                                </a>
                            </li>
                            <li>
                                <a href="#international-data-transfers">
                                    International Data Transfers
                                </a>
                            </li>
                            <li>
                                <a href="#children-section">Children</a>
                            </li>
                            <li>
                                <a href="#changes-to-this-privacy-policy">
                                    Changes to this Privacy Policy
                                </a>
                            </li>
                            <li>
                                <a href="#how-to-contact-us">
                                    How to Contact Us
                                </a>
                            </li>
                            <li>
                                <a href="#california-resident-info">
                                    Information for California Residents
                                </a>
                            </li>
                            <li>
                                <a href="#notice-to-european-users">
                                    Additional Information for European Union
                                    Users
                                </a>
                            </li>
                            <li>
                                <a href="/cookie-policy">Cookie Policy</a>
                            </li>
                        </ul>
                        <h3 id="personal-information-we-collect">
                            Personal Information We Collect
                        </h3>
                        <p>
                            <strong>Information you provide to us.</strong>
                            &nbsp;&nbsp;Personal information about you provide
                            to us through the Service or otherwise includes:
                        </p>
                        <ul>
                            <li>
                                <strong>Contact data</strong>, such as your
                                first and last name, email and mailing
                                addresses, phone number, professional title and
                                company name.
                            </li>
                            <li>
                                <strong>Profile data</strong>, such as your
                                username and password that you may set to
                                establish an online account with us, your
                                interests and preferences, and any content
                                uploaded to the Service (such as text, images,
                                audio, and video, along with the metadata
                                associated with the files you upload).
                            </li>
                            <li>
                                <strong>Registration data</strong>, such as
                                information that may be related to a service, an
                                account or an event you register for (such as a
                                Virtual Career Fair).
                            </li>
                            <li>
                                <strong>Communications</strong>, such as
                                information you provide when you contact us with
                                questions, feedback, or otherwise correspond
                                with us online.
                            </li>
                            <li>
                                <strong>Marketing data</strong>, such as
                                information such your preferences for receiving
                                communications about our activities, events, and
                                publications, and details about how you engage
                                with our communications.
                            </li>
                            <li>
                                <strong>Professional data</strong>, such as
                                professional credentials and skills, educational
                                and work history, and other information of the
                                type that may be included on a resume or
                                curriculum vitae.
                            </li>
                            <li>
                                <strong>Content</strong> that you post or upload
                                to areas of the Service visible to other users,
                                such as reviews or comments.
                            </li>
                            <li>
                                <strong>Other information</strong> that we may
                                collect which is not specifically listed here,
                                but which we will use in accordance with this
                                Privacy Policy or as otherwise disclosed at the
                                time of collection.
                            </li>
                        </ul>
                        <p>
                            <strong>Third party sources.</strong>&nbsp;&nbsp;We
                            may combine personal information we receive from you
                            with personal information we obtain from other
                            sources such as:
                        </p>
                        <ul>
                            <li>
                                <strong>Public sources</strong>, such as social
                                media platforms.
                            </li>
                            <li>
                                <strong>Business partners</strong>, such as
                                joint marketing partners, event co-sponsors and
                                Virtual Career Fair employers.
                            </li>
                        </ul>
                        <p>
                            <strong>Automatic data collection.</strong>
                            &nbsp;&nbsp;We, our service providers, and our
                            business partners may automatically log information
                            about you, your computer or mobile device, and your
                            activity over time on our sites, app, and online
                            services, such as:
                        </p>
                        <ul>
                            <li>
                                <strong>Device data</strong>, such as your
                                computer or mobile device operating system type
                                and version number, manufacturer and model,
                                browser type, screen resolution, IP address, IP
                                address, unique identifiers, the website you
                                visited before browsing to our website, and
                                general location information such as city, state
                                or geographic area.
                            </li>
                            <li>
                                <strong>Online activity data</strong>, such as
                                pages or screens you viewed, how long you spent
                                on a page or screen, navigation paths between
                                pages or screens, information about your
                                activity on a page or screen, access times, and
                                duration of access.
                            </li>
                            <li>
                                <strong>Precise geolocation data</strong>, such
                                as when you authorize our mobile application to
                                access your location.
                            </li>
                        </ul>
                        <p>
                            <strong>Cookies and similar technologies.</strong>
                            &nbsp;&nbsp;Like many online services, we use the
                            following technologies:
                        </p>
                        <ul>
                            <li>
                                <strong>Cookies</strong>, which are text files
                                that websites store on a visitor‘s device to
                                uniquely identify the visitor’s browser or to
                                store information or settings in the browser for
                                the purpose of helping you navigate between
                                pages efficiently, remembering your preferences,
                                enabling functionality and helping us understand
                                user activity and patterns.
                            </li>
                            <li>
                                <strong>Flash cookies</strong>, or
                                locally-stored objects, which are used on
                                websites for purposes similar to cookies but
                                allow storage of a larger amount of data.
                            </li>
                            <li>
                                <strong>Web beacons</strong>, also known as
                                pixel tags or clear GIFs, which are used to
                                demonstrate that a webpage or email was accessed
                                or opened, or that certain content was viewed or
                                clicked.
                            </li>
                        </ul>
                        <p>
                            See our <a href="/cookie-policy">Cookie Policy</a>{' '}
                            for more information.
                        </p>
                        <p>
                            <strong>Sensitive personal information.</strong>
                            &nbsp;&nbsp;We ask that you not share with us any
                            sensitive personal information (e.g., social
                            security numbers, information related to racial or
                            ethnic origin, political opinions, religion or other
                            beliefs, health, biometrics or genetic
                            characteristics, criminal background or union
                            membership) on or through the Service or otherwise.
                            If you do anyway, you must consent to our processing
                            and use of such sensitive personal information in
                            accordance with this Privacy Policy.
                        </p>
                        <h3 id="how-we-use-personal-info">
                            How We Use Your Personal Information
                        </h3>
                        <p>
                            We use your personal information for the following
                            purposes and as otherwise described in this Privacy
                            Policy or at the time of collection:
                        </p>
                        <p>
                            <strong>Service delivery.</strong>&nbsp;&nbsp;We use
                            your personal information to:
                        </p>
                        <ul>
                            <li>
                                provide, operate and improve the Service and our
                                business;
                            </li>
                            <li>
                                provide information about our products and
                                services;
                            </li>
                            <li>
                                establish and maintain your user profile on the
                                Service;
                            </li>
                            <li>
                                communicate with you about the Service,
                                including by sending you announcements, updates,
                                security alerts, and support and administrative
                                messages;
                            </li>
                            <li>
                                communicate with you about events in which you
                                participate;
                            </li>
                            <li>
                                understand your needs and interests, and
                                personalize your experience with the Service and
                                our communications;
                            </li>
                            <li>
                                provide support and maintenance for the Service;
                            </li>
                            <li>
                                to respond to your requests, questions and
                                feedback;
                            </li>
                        </ul>

                        <p>
                            <strong>Research and development.</strong>
                            &nbsp;&nbsp;We may use your personal information for
                            research and development purposes, including to
                            analyze and improve the Service and our business. As
                            part of these activities, we may create aggregated,
                            de-identified or other anonymous data from personal
                            information we collect. We make personal information
                            into anonymous data by removing information that
                            makes the data personally identifiable to you. We
                            may use this anonymous data and share it with third
                            parties for our lawful business purposes, including
                            to analyze and improve the Service and promote our
                            business.
                        </p>
                        <p>
                            <strong>Marketing</strong>&nbsp;&nbsp;We may collect
                            and use your personal information for marketing
                            purposes:
                        </p>
                        <ul>
                            <li>
                                <strong>Direct marketing.</strong> We may send
                                you Fairygodboss-related or other direct
                                marketing communications as permitted by law.
                                You may opt-out of our marketing communications
                                as described in the{' '}
                                <a href="#your-choices">Opt out of marketing</a>
                                &nbsp; section below.
                            </li>
                        </ul>
                        <p>
                            <strong>Compliance and protection.</strong>
                            &nbsp;&nbsp;We may use your personal information to:
                        </p>
                        <ul>
                            <li>
                                protect our, your or others’ rights, privacy,
                                safety or property (including by making and
                                defending legal claims);
                            </li>
                            <li>
                                audit our internal processes for compliance with
                                legal and contractual requirements and internal
                                policies;
                            </li>
                            <li>enforce our Terms of Service;</li>
                            <li>
                                protect, investigate and deter against
                                fraudulent, harmful, unauthorized, unethical or
                                illegal activity, including cyberattacks and
                                identity theft; and
                            </li>
                            <li>
                                comply with applicable laws, lawful requests and
                                legal process, such as to respond to subpoenas
                                or requests from government authorities.
                            </li>
                        </ul>
                        <p>
                            <strong>Other purposes with your consent.</strong>
                            &nbsp;&nbsp;In some cases we will ask for your
                            consent to collect, use or share your personal
                            information for other purposes. For example, we may
                            ask for your consent to send you marketing emails
                            where required by law or to post your testimonial or
                            endorsement.
                        </p>

                        <h3 id="how-we-share-personal-info">
                            How We Share your Personal Information
                        </h3>
                        <p>
                            We do not share your personal information with third
                            parties without your consent, except in the
                            following circumstances or as described in this
                            Privacy Policy:
                        </p>
                        <ul>
                            <li>
                                <strong>Affiliates.</strong>&nbsp;&nbsp;We may
                                share your personal information with our
                                corporate parent, subsidiaries, and affiliates,
                                for purposes consistent with this Privacy
                                Policy.
                            </li>
                            <li>
                                <strong>Service Providers.</strong>
                                &nbsp;&nbsp;We may share your personal
                                information with third party companies and
                                individuals that provide services on our behalf
                                or help us operate the Services (such as
                                customer support, hosting, analytics, email
                                delivery, marketing, and database management
                                services). These third parties may use your
                                personal information only as directed or
                                authorized by us and in a manner consistent with
                                this Privacy Policy, and are prohibited from
                                using or disclosing your information for any
                                other purpose.
                            </li>
                            <li>
                                <strong>Employers.</strong>&nbsp;&nbsp;We may
                                disclose any information provided by job
                                applicants through their professional profiles,
                                or during a Virtual Career Fair (i.e.,
                                professional data, as discussed above), to
                                recruiters from participating companies.
                            </li>
                            <li>
                                <strong>Other users and the public.</strong>
                                &nbsp;&nbsp;Other parties may be able to view
                                your reviews, comments or other content on areas
                                of the Service accessible to other Fairygodboss
                                users or the public, where it can be copied and
                                retained even after you modify or delete such
                                reviews, comments or other content. In addition,
                                unless you affirmatively opt out of displaying
                                your username at the time you publish any such
                                content, your username will be displayed to
                                other users through the Service, and other users
                                may be able to contact you through the Service.
                                We do not control how other users or third
                                parties use any personal information that you
                                make available to them. Please be aware that any
                                information you post publicly can be cached,
                                copied, screen captured or stored elsewhere by
                                others (e.g., search engines) before you have a
                                chance to edit or remove it.
                            </li>
                            <li>
                                <strong>Third party platforms.</strong>
                                &nbsp;&nbsp;Social media platforms or other
                                third party platforms that you connect to the
                                Service and where you authorize us to share your
                                information with them (such as when you use
                                options to access the Service by logging into
                                the third party platform).
                            </li>
                            <li>
                                <strong>Professional advisors.</strong>
                                &nbsp;&nbsp;We may disclose your personal
                                information to professional advisors, such as
                                lawyers, bankers, auditors and insurers, where
                                necessary in the course of the professional
                                services they render to us.
                            </li>
                            <li>
                                <strong>Compliance and protection.</strong>
                                &nbsp;&nbsp;We may disclose your personal
                                information as we believe appropriate to
                                government or law enforcement officials or
                                private parties (a) for the compliance and
                                protection purposes described above; (b) as
                                required by law, lawful requests or legal
                                process, such as to respond to subpoenas or
                                requests from government authorities; (c) where
                                permitted by law in connection with any legal
                                investigation; and (d) to prosecute or defend
                                legal claims.
                            </li>
                            <li>
                                <strong>Business transferees.</strong>
                                &nbsp;&nbsp;We may sell, transfer or otherwise
                                share some or all of our business or assets,
                                including your personal information, in
                                connection with a business transaction (or
                                potential business transaction) such as a
                                corporate divestiture, merger, consolidation,
                                acquisition, reorganization or sale of assets,
                                or in the event of bankruptcy or dissolution. In
                                such a case, we will make reasonable efforts to
                                require the recipient to honor this Privacy
                                Policy.
                            </li>
                        </ul>

                        <h3 id="your-choices">Your Choices</h3>
                        <p>
                            In this section, we describe the rights and choices
                            available to all users.
                        </p>
                        <p>
                            <strong>Access or update Your information.</strong>
                            &nbsp;&nbsp;If you have registered for an account
                            with us, you may review and update certain personal
                            information in your account profile by logging into
                            the account.
                        </p>
                        <p>
                            <strong>
                                Opt out of marketing communications.
                            </strong>
                            &nbsp;&nbsp;You may opt out of marketing-related
                            emails by following the opt-out or unsubscribe
                            instructions at the bottom of the email, or by
                            contacting us at{' '}
                            <a href="mailto:info@fairygodboss.com">
                                info@fairygodboss.com
                            </a>
                            . You may continue to receive service-related and
                            other non-marketing emails.
                        </p>
                        <p>
                            <strong>Cookies.</strong>&nbsp;&nbsp;We may allow
                            service providers and other third parties to use
                            cookies and similar technologies to track your
                            browsing activity over time and across the Service
                            and third party websites. For more details, see
                            our&nbsp;
                            <a href="/cookie-policy">Cookie Policy.</a>.
                        </p>
                        <p>
                            <strong>Do Not Track.</strong>&nbsp;&nbsp;Some
                            Internet browsers may be configured to send “Do Not
                            Track” signals to the online services that you
                            visit. We currently do not respond to “Do Not Track”
                            or similar signals. To find out more about “Do Not
                            Track,” please visit&nbsp;
                            <a href="https://www.allaboutdnt.com">
                                http://www.allaboutdnt.com
                            </a>
                            .
                        </p>
                        <p>
                            <strong>
                                Declining to provide your personal information.
                            </strong>
                            &nbsp;&nbsp;Where we are required by law to collect
                            your personal information, or where we need your
                            personal information in order to provide the Service
                            to you, if you do not provide this information when
                            requested (or you later ask to delete it), we may
                            not be able to provide you with our services. We
                            will tell you what information you must provide to
                            receive the Service by designating it as required at
                            the time of collection or through other appropriate
                            means.
                        </p>
                        <p>
                            <strong>Connecting via Facebook or Google.</strong>
                            &nbsp;&nbsp;If you choose to connect to the Service
                            via Facebook, Google or any other social media
                            service, you may have the ability to limit the
                            information that we may obtain from the third party
                            at the time you login to the Service using the third
                            party’s authentication service or otherwise connect
                            your account. Subsequently, you may be able to
                            control your settings through the third party’s
                            platform or service. For example, you may access and
                            change your settings through the Facebook&nbsp;
                            <a href="https://www.facebook.com/settings?tab=applications">
                                settings page
                            </a>
                            . If you withdraw our ability to access certain
                            information from a third party platform or social
                            media network, that choice will not apply to
                            information that we have already received from that
                            third party.
                        </p>

                        <h3 id="third-party-sites-and-services">
                            Other Sites and Services
                        </h3>
                        <p>
                            The Service may contain links to other websites and
                            other online services operated by third parties.
                            These links are not an endorsement of, or
                            representation that we are affiliated with, any
                            third party. In addition, our content may be
                            included on web pages or online services that are
                            not associated with us. We do not control third
                            party websites or online services, and we are not
                            responsible for their actions. Other websites and
                            services follow different rules regarding the
                            collection, use and sharing of your personal
                            information. We encourage you to read the privacy
                            policies of the other websites and online services
                            you use.
                        </p>

                        <h3 id="security-practices">Security Practices</h3>
                        <p>
                            The security of your personal information important
                            to us. We take a number of organizational, technical
                            and physical measures designed to protect the
                            personal information we collect, both during
                            transmission and once we receive it. However, no
                            security safeguards are 100% secure and we cannot
                            guarantee the security of your information.
                        </p>

                        <h3 id="international-data-transfers">
                            International Data Transfers
                        </h3>
                        <p>
                            We are headquartered in the United States and have
                            service providers in other countries, and your
                            personal information may be transferred to the
                            United States or other locations outside of your
                            state, province, or country where privacy laws may
                            not be as protective as those in your state,
                            province, or country.
                        </p>
                        <p>
                            European Union users should read the important
                            information provided&nbsp;
                            <a href="#notice-to-european-users">below</a> about
                            transfer of personal information outside of the
                            European Union.
                        </p>

                        <h3 id="children-section">Children</h3>
                        <p>
                            The Service is not directed to, and Fairygodboss
                            does not knowingly collect personal information
                            from, anyone under the age of 16. If a parent or
                            guardian becomes aware that his or her child has
                            provided us with information without their consent,
                            he or she should contact us. We will delete such
                            information from our files as soon as reasonably
                            practicable. We encourage parents with concerns to
                            contact us.
                        </p>

                        <h3 id="changes-to-this-privacy-policy">
                            Changes to this Privacy Policy.
                        </h3>
                        <p>
                            We reserve the right to modify this Privacy Policy
                            at any time. If we make material changes to this
                            Privacy Policy, we will notify you by updating the
                            date of this Privacy Policy and posting it on the
                            Service. We may also provide notification of changes
                            in another way that we believe is reasonably likely
                            to reach you, such as via e-mail (if you have an
                            account where we have your contact information) or
                            another manner through the Services.
                        </p>
                        <p>
                            Any modifications to this Privacy Policy will be
                            effective upon our posting the new terms and/or upon
                            implementation of the new changes on the Services
                            (or as otherwise indicated at the time of posting).
                            In all cases, your continued use of the Services
                            after the posting of any modified Privacy Policy
                            indicates your acceptance of the terms of the
                            modified Privacy Policy.
                        </p>

                        <h3 id="how-to-contact-us">How to Contact Us</h3>
                        <p>
                            Please direct any questions or comments about this
                            Policy or privacy practices to&nbsp;
                            <a href="mailto:info@fairygodboss.com">
                                info@fairygodboss.com
                            </a>
                            . You may also write to us via postal mail at:
                        </p>
                        <div className="address-box">
                            <p>FGB Muse Group Inc.</p>
                            <p>Attn: Legal</p>
                            <p>
                                114 Rayson Street, 1<sup>st</sup> Floor
                            </p>
                            <p>Northville, MI 48167</p>
                        </div>

                        <h3 id="california-resident-info">
                            Information for California Residents
                        </h3>
                        <p>
                            This section applies only to California residents.
                            It describes how we collect, use and share Personal
                            Information of California residents in operating our
                            business, and their rights with respect to that
                            Personal Information. For purposes of this section,
                            “<strong>Personal Information</strong>” has the
                            meaning given in the California Consumer Privacy Act
                            of 2018 (“<strong>CCPA</strong>”) but does not
                            include information exempted from the scope of the
                            CCPA.
                        </p>
                        <p>
                            <strong>Your California privacy rights.</strong> As
                            a California resident, you have the rights listed
                            below. However, these rights are not absolute, and
                            in certain cases we may decline your request as
                            permitted by law.
                        </p>
                        <ul>
                            <li>
                                <strong>Information.</strong>&nbsp;&nbsp;You can
                                request the following information about how we
                                have collected and used your Personal
                                Information during the past 12 months:
                                <ul>
                                    <li>
                                        The categories of Personal Information
                                        that we have collected.
                                    </li>
                                    <li>
                                        The categories of sources from which we
                                        collected Personal Information.
                                    </li>
                                    <li>
                                        The business or commercial purpose for
                                        collecting and/or selling Personal
                                        Information.
                                    </li>
                                    <li>
                                        The categories of third parties with
                                        whom we share Personal Information.
                                    </li>
                                    <li>
                                        Whether we have disclosed your Personal
                                        Information for a business purpose, and
                                        if so, the categories of Personal
                                        Information received by each category of
                                        third party recipient.
                                    </li>
                                    <li>
                                        Whether we’ve sold your Personal
                                        Information, and if so, the categories
                                        of Personal Information received by each
                                        category of third party recipient.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>Access.</strong>&nbsp;&nbsp;You can
                                request a copy of the Personal Information that
                                we have collected about you during the past 12
                                months.
                            </li>
                            <li>
                                <strong>Deletion.</strong>&nbsp;&nbsp;You can
                                ask us to delete the Personal Information that
                                we have collected from you.
                            </li>
                            <li>
                                <strong>Nondiscrimination.</strong>
                                &nbsp;&nbsp;You are entitled to exercise the
                                rights described above free from discrimination
                                in the form of legally prohibited increases in
                                the price or decreases in the quality of our
                                Service.
                            </li>
                        </ul>
                        <p>
                            <strong>How to exercise your rights</strong>
                        </p>
                        <p>
                            You may exercise your California privacy rights
                            described above as follows:
                        </p>
                        <ul>
                            <li>
                                <strong>
                                    Right to information, access, and deletion.
                                </strong>
                                &nbsp;&nbsp;You can request to exercise your
                                information, access and deletion rights by
                                emailing us at&nbsp;
                                <a href="mailto:info@fairygodboss.com">
                                    info@fairygodboss.com
                                </a>
                                .
                            </li>
                        </ul>
                        <p>
                            We reserve the right to confirm your California
                            residence to process your requests and will need to
                            confirm your identity to process your requests to
                            exercise your information, access or deletion
                            rights. As part of this process, government
                            identification may be required. Consistent with
                            California law, you may designate an authorized
                            agent to make a request on your behalf. In order to
                            designate an authorized agent to make a request on
                            your behalf, you must provide a valid power of
                            attorney, the requester’s valid government-issued
                            identification, and the authorized agent’s valid
                            government issued identification. We cannot process
                            your request if you do not provide us with
                            sufficient detail to allow us to understand and
                            respond to it.
                        </p>
                        <p>
                            <strong>
                                Personal information that we collect, use and
                                share
                            </strong>
                        </p>
                        <p>
                            We are required to explain the categories of
                            Personal Information we collect by reference to the
                            categories specified by the CCPA (in bold), as
                            defined&nbsp;
                            <a href="https://leginfo.legislature.ca.gov/faces/codes_displayText.xhtml?lawCode=CIV&division=3.&title=1.81.5.&part=4.&chapter=&article=">
                                here
                            </a>
                            , which are described in the list below. For each
                            category, we list the type of data we collect and
                            the source of that data as described in the section
                            above entitled&nbsp;
                            <a href="#personal-information-we-collect">
                                Personal Information We Collect
                            </a>
                            . We have not sold your personal information in the
                            preceding 12 months.
                        </p>
                        <ul>
                            <li>
                                <strong>Identifiers</strong>
                                <ul>
                                    <li>
                                        <em>What we collect:</em> Contact data,
                                        profile data
                                    </li>
                                    <li>
                                        <em>Sources:</em> You, third party
                                        platforms, business partners
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>Commercial information</strong>
                                <ul>
                                    <li>
                                        <em>What we collect:</em> Contact data,
                                        profile data, online activity data
                                    </li>
                                    <li>
                                        <em>Sources:</em> You, automatic
                                        collection, third party platforms
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>Online identifiers</strong>
                                <ul>
                                    <li>
                                        <em>What we collect:</em> Contact data,
                                        profile data, online activity data,
                                        device data
                                    </li>
                                    <li>
                                        <em>Sources:</em> You, automatic
                                        collection, third party platforms
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>Internet or network information</strong>
                                <ul>
                                    <li>
                                        <em>What we collect:</em> Device data,
                                        online activity data
                                    </li>
                                    <li>
                                        <em>Sources:</em> Automatic collection
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>Inferences</strong> drawn from any of
                                the above information to create a profile
                                reflecting your preferences, characteristics,
                                and behavior.
                                <ul>
                                    <li>
                                        <em>May be derived from:</em> Device
                                        data, online activity data
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>
                                    Professional or Employment information
                                </strong>
                                <ul>
                                    <li>
                                        <em>What we collect:</em> Contact data,
                                        profile data, professional data
                                    </li>
                                    <li>
                                        <em>Sources:</em> You, third party
                                        platforms
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>
                                    Protected Classification Characteristics
                                </strong>
                                , which we do not intentionally collect, but it
                                may be revealed in other data we collect.
                            </li>
                        </ul>

                        <h3 id="notice-to-european-users">
                            Additional Information for European Union Users
                        </h3>
                        <p>
                            The information provided in this section applies
                            only to individuals in Europe.
                        </p>
                        <p>
                            <strong>Personal information.</strong>
                            &nbsp;&nbsp;References to “personal information” in
                            this Privacy Policy are equivalent to “personal
                            data” governed by European data protection
                            legislation.
                        </p>
                        <p>
                            <strong>Controller.</strong>
                            &nbsp;&nbsp;FGB Muse Group Inc. is the controller of
                            your personal information covered by this Privacy
                            Policy for purposes of European data protection
                            legislation.
                        </p>
                        <p>
                            <strong>Legal bases for processing.</strong>
                            &nbsp;&nbsp;We use your personal information only as
                            permitted by law. Our legal bases for processing the
                            personal information described in this Privacy
                            Policy are described in the table below.
                        </p>
                        <div className="eu-legal-box-wrapper">
                            <div className="row box-heading">
                                <div className="column col-title">
                                    <p>
                                        <strong>Processing Purpose</strong>
                                    </p>
                                    <p>
                                        <em>
                                            Details regarding each processing
                                            purpose listed below are provided in
                                            the section above titled “How We Use
                                            Your Personal Information.”
                                        </em>
                                    </p>
                                </div>
                                <div className="column">
                                    <p>
                                        <strong>Legal basis</strong>
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="column col-title">
                                    <p>
                                        <strong>Service delivery</strong>
                                    </p>
                                </div>
                                <div className="column">
                                    <p>
                                        Processing is necessary to perform the
                                        contract governing our provision of the
                                        Services or to take steps that you
                                        request prior to signing up for the
                                        Services. If we have not entered into a
                                        contract with you, we process your
                                        personal information based on our
                                        legitimate interest in providing the
                                        Service you access and request.
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="column col-title">
                                    <p>
                                        <strong>Marketing</strong>
                                    </p>
                                </div>
                                <div className="column">
                                    <p>
                                        Processing is based on your consent
                                        where that consent is required by
                                        applicable law. Where we rely on your
                                        consent you have the right to withdraw
                                        it any time in the manner indicated when
                                        you consent or in the Service. Where
                                        such consent is not required by
                                        applicable law, we process your personal
                                        information for these purposes based on
                                        our legitimate interests in promoting
                                        our business.
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="column col-title">
                                    <p>
                                        <strong>
                                            Research and development
                                        </strong>
                                    </p>
                                </div>
                                <div className="column">
                                    <p>
                                        Processing is based on our legitimate
                                        interests in performing research and
                                        development to improve our services and
                                        develop new services.
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="column col-title">
                                    <p>
                                        <strong>
                                            Compliance and protection
                                        </strong>
                                    </p>
                                </div>
                                <div className="column">
                                    <p>
                                        Processing is necessary to comply with
                                        our legal obligations or based on our
                                        legitimate interests in protecting our
                                        or others’ rights, privacy, safety or
                                        property.
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="column col-title">
                                    <p>
                                        <strong>
                                            Other purposes with your consent
                                        </strong>
                                    </p>
                                </div>
                                <div className="column">
                                    <p>
                                        Processing is based on your consent.
                                        Where we rely on your consent you have
                                        the right to withdraw it any time in the
                                        manner indicated when you consent or in
                                        the Service.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <p>
                            <strong>Use for new purposes.</strong>&nbsp;&nbsp;We
                            may use your personal information for reasons not
                            described in this Privacy Policy where permitted by
                            law and the reason is compatible with the purpose
                            for which we collected it. If we need to use your
                            personal information for an unrelated purpose, we
                            will notify you and explain the applicable legal
                            basis.
                        </p>
                        <strong>Retention</strong>
                        <p>
                            We retain personal information for as long as
                            necessary to fulfill the purposes for which we
                            collected it, including for the purposes of
                            satisfying any legal, accounting, or reporting
                            requirements, to establish or defend legal claims,
                            or for fraud prevention purposes.
                        </p>
                        <p>
                            To determine the appropriate retention period for
                            personal information, we consider the amount,
                            nature, and sensitivity of the personal information,
                            the potential risk of harm from unauthorized use or
                            disclosure of your personal information, the
                            purposes for which we process your personal
                            information and whether we can achieve those
                            purposes through other means, and the applicable
                            legal requirements.
                        </p>
                        <p>
                            When we no longer require the personal information
                            we have collected about you, we will either delete
                            or anonymize it or, if this is not possible (for
                            example, because your personal information has been
                            stored in backup archives), then we will securely
                            store your personal information and isolate it from
                            any further processing until deletion is possible.
                            If we anonymize your personal information (so that
                            it can no longer be associated with you), we may use
                            this information indefinitely without further notice
                            to you.
                        </p>
                        <strong>Your rights</strong>
                        <p>
                            European data protection laws give you certain
                            rights regarding your personal information. If you
                            are located within the European Union, you may ask
                            us to take the following actions in relation to your
                            personal information that we hold:
                        </p>
                        <ul>
                            <li>
                                <strong>Access.</strong>&nbsp;&nbsp;Provide you
                                with information about our processing of your
                                personal information and give you access to your
                                personal information.
                            </li>
                            <li>
                                <strong>Correct.</strong>&nbsp;&nbsp;Update or
                                correct inaccuracies in your personal
                                information.
                            </li>
                            <li>
                                <strong>Delete.</strong>&nbsp;&nbsp;Delete your
                                personal information.
                            </li>
                            <li>
                                <strong>Transfer.</strong>&nbsp;&nbsp;Transfer a
                                machine-readable copy of your personal
                                information to you or a third party of your
                                choice.
                            </li>
                            <li>
                                <strong>Restrict.</strong>&nbsp;&nbsp;Restrict
                                the processing of your personal information.
                            </li>
                            <li>
                                <strong>Object.</strong>&nbsp;&nbsp;Object to
                                our reliance on our legitimate interests as the
                                basis of our processing of your personal
                                information that impacts your rights.
                            </li>
                        </ul>
                        <p>
                            You may submit these requests by email or to our
                            postal address provided above. We may request
                            specific information from you to help us confirm
                            your identity and process your request. Applicable
                            law may require or permit us to decline your
                            request. If we decline your request, we will tell
                            you why, subject to legal restrictions. If you would
                            like to submit a complaint about our use of your
                            personal information or our response to your
                            requests regarding your personal information, you
                            may contact us or submit a complaint to the data
                            protection regulator in your jurisdiction. You can
                            find your data protection regulator&nbsp;
                            <a href="https://edpb.europa.eu/about-edpb/board/members_en">
                                here
                            </a>
                            .
                        </p>
                        <strong>Cross-Border Data Transfer</strong>
                        <p>
                            If we transfer your personal information out of
                            Europe to a country not deemed by the European
                            Commission to provide an adequate level of personal
                            information protection, the transfer will be
                            performed:
                        </p>
                        <ul>
                            <li>
                                Pursuant to the recipient’s compliance with
                                standard contractual clauses, EU-US Privacy
                                Shield (or Swiss-US Privacy Shield, as
                                applicable) or Binding Corporate Rules
                            </li>
                            <li>
                                Pursuant to the consent of the individual to
                                whom the personal information pertains
                            </li>
                            <li>
                                As otherwise permitted by applicable European
                                requirements.
                            </li>
                        </ul>
                        <p>
                            You may contact us if you want further information
                            on the specific mechanism used by us when
                            transferring your personal information out of
                            Europe.
                        </p>
                    </PageSection>
                </Layout>
            </div>
        );
    }
}

PrivacyPolicy.propTypes = {
    PAGE_PROPS: PropTypes.shape().isRequired,
};
